import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import axios from 'axios';
import { API_Root } from '../../globalState';

export const AdditionalCharges = ({ tripId, userId, canEdit = false }) => {
   const [charges, setCharges] = useState([]);
   const [showAdd, setShowAdd] = useState(false);
   const [description, setDescription] = useState('');
   const [amount, setAmount] = useState(null);
   const [deleteId, setDeleteId] = useState(null);
   const [busy, setBusy] = useState(false);
   const [loading, setLoading] = useState(true);

   const getCharges = (silent = false) => {
      if (!tripId || !userId) {
         setLoading(false);
         return;
      }
      if (!silent) {
         setLoading(true);
      }
      var apiUrl = `${API_Root}api/trip/GetAdditionalCharges?tripId=${tripId}&userId=${userId}`;
      axios.get(apiUrl).then((values) => {
         setCharges(values.data || []);
      }).catch(() => {
         if (!silent) {
            setCharges([]);
         }
      }).finally(() => {
         setLoading(false);
      });
   };

   useEffect(() => {
      getCharges();
   }, [tripId, userId]);

   const closeAdd = () => {
      setShowAdd(false);
      setDescription('');
      setAmount(null);
   };

   const addCharge = () => {
      if (!tripId || !userId || !description || !amount) {
         return;
      }
      setBusy(true);
      axios.post(`${API_Root}api/trip/AddAdditionalCharge`, {
         tripId: tripId,
         createdBy: userId,
         dollarAmount: amount,
         chargeDescription: description
      }).then(() => {
         setBusy(false);
         closeAdd();
         getCharges(true);
      }).catch(() => setBusy(false));
   };

   const deleteCharge = () => {
      if (!deleteId) {
         return;
      }
      setBusy(true);
      axios.get(`${API_Root}api/trip/DeleteAdditionalCharge?id=${deleteId}`).then(() => {
         setBusy(false);
         setDeleteId(null);
         getCharges(true);
      }).catch(() => setBusy(false));
   };

   const formatAmount = (n) => {
      return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
   };

   return (
      <div>
         {loading ? (
            <p className="job-card-hint mt-0"><i className="pi pi-spin pi-spinner mr-2"></i>Loading charges…</p>
         ) : charges.length === 0 && (
            <p className="job-card-hint mt-0">No additional charges yet.</p>
         )}
         {charges.map((c) => (
            <div key={c.id} className="job-vehicle-row">
               <div className="job-vehicle-info">
                  <span className="job-vehicle-name">{formatAmount(c.dollarAmount)}</span>
                  <span className="job-card-meta" style={{ marginTop: 0 }}>{c.chargeDescription}</span>
               </div>
               {canEdit ? (
               <Button
                  icon="pi pi-trash"
                  text
                  rounded
                  severity="danger"
                  aria-label="Delete charge"
                  onClick={() => setDeleteId(c.id)}
               />
               ) : null}
            </div>
         ))}
         {canEdit ? (
         <Button
            label="Add charge"
            icon="pi pi-plus"
            size="small"
            className="mt-2"
            onClick={() => setShowAdd(true)}
         />
         ) : null}
         <Dialog
            header="Add additional charge"
            visible={showAdd}
            className="sm:w-11 md:w-6 lg:w-4"
            onHide={() => { if (!busy) closeAdd(); }}
            footer={(
               <div className="flex justify-content-end gap-2">
                  <Button label="Cancel" className="p-button-text" onClick={() => closeAdd()} disabled={busy} />
                  <Button
                     label="Save"
                     onClick={() => addCharge()}
                     loading={busy}
                     disabled={busy || !description || !amount}
                  />
               </div>
            )}>
            <div className="field">
               <label htmlFor="charge-desc" className="block mb-2">Description</label>
               <InputText
                  id="charge-desc"
                  className="w-full"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
               />
            </div>
            <div className="field mb-0">
               <label htmlFor="charge-amt" className="block mb-2">Amount</label>
               <InputNumber
                  id="charge-amt"
                  className="w-full"
                  inputClassName="w-full"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  min={0}
                  value={amount}
                  onValueChange={(e) => setAmount(e.value)}
               />
            </div>
         </Dialog>
         <Dialog
            header="Delete charge"
            visible={!!deleteId}
            className="sm:w-11 md:w-5 lg:w-3"
            onHide={() => { if (!busy) setDeleteId(null); }}
            footer={(
               <div className="flex justify-content-end gap-2">
                  <Button label="Cancel" className="p-button-text" onClick={() => setDeleteId(null)} disabled={busy} />
                  <Button label="Delete" severity="danger" onClick={() => deleteCharge()} loading={busy} disabled={busy} />
               </div>
            )}>
            <p className="m-0">Are you sure you want to delete this additional charge?</p>
         </Dialog>
      </div>
   );
};
